import type { FC } from 'react'
import { useState, useEffect } from 'react'
import type { AnimationItem } from 'lottie-web'

import styled from '@emotion/styled'

import Button from '@segment/matcha/components/Button'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import { trackButtonClick } from '@segment/tracking/scripts'

import useIsInView from '@segment/utils/useInView'

import useLottie from '@segment/utils/useLottie/useLottie'

import jsonUrl from './assets/personalization-hero-lottie.json.data'

const data = {
  title: `2021 State of Personalization Report`,
  description: `From nice-to-have to necessity for intelligent customer engagement.`,
  button: {
    title: `Download the report`,
    href: `https://learn.segment.com/state-of-personalization-2021/`,
  },
}

const Hero: FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])

  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])

  return (
    <Wrapper>
      <HeroBase variant="2Col">
        <HeroRecipe.Copy>
          <CopyTextBase>
            <CopyText.Heading tag="h1" variant="h1">
              {data.title}
            </CopyText.Heading>
            <CopyText.Description tag="p" variant="pExtraLarge">
              {data.description}
            </CopyText.Description>
            <Button
              color="primary"
              size="large"
              variant="filled"
              href={data.button.href}
              onClick={() => trackButtonClick({ buttonText: data.button.title, targetUrl: data.button.href })}
            >
              {data.button.title}
            </Button>
          </CopyTextBase>
        </HeroRecipe.Copy>
        <HeroRecipe.Contents>
          <Animation ref={containerRef} />
        </HeroRecipe.Contents>
      </HeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.green[10]};
`

const Animation = styled.div`
  display: grid;
  width: 100%;
  aspect-ratio: 580 / 582;
`

export default Hero
